'use client';

import React, { useState } from 'react';
import Link from 'next/link';

interface Property {
  id: string;
  name: string;
  location: string;
  type: string;
  status: 'active' | 'inactive' | 'pending';
  price: number;
  rating: number;
  reviews: number;
  occupancy: number;
  image: string;
  imageAlt: string;
  bedrooms: number;
  bathrooms: number;
  guests: number;
}

const properties: Property[] = [
  {
    id: '1',
    name: 'Lakeside Villa Entebbe',
    location: 'Entebbe, Uganda',
    type: 'Villa',
    status: 'active',
    price: 180000,
    rating: 4.9,
    reviews: 47,
    occupancy: 82,
    image: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=400&q=80',
    imageAlt: 'Luxury lakeside villa with pool overlooking Lake Victoria in Entebbe',
    bedrooms: 4,
    bathrooms: 3,
    guests: 8,
  },
  {
    id: '2',
    name: 'Kampala City Apartment',
    location: 'Kololo, Kampala',
    type: 'Apartment',
    status: 'active',
    price: 95000,
    rating: 4.7,
    reviews: 31,
    occupancy: 74,
    image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400&q=80',
    imageAlt: 'Modern city apartment with panoramic views of Kampala skyline',
    bedrooms: 2,
    bathrooms: 2,
    guests: 4,
  },
  {
    id: '3',
    name: 'Bwindi Forest Lodge',
    location: 'Bwindi, Uganda',
    type: 'Lodge',
    status: 'active',
    price: 250000,
    rating: 5.0,
    reviews: 19,
    occupancy: 91,
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=400&q=80',
    imageAlt: 'Rustic forest lodge surrounded by lush Bwindi Impenetrable Forest',
    bedrooms: 3,
    bathrooms: 2,
    guests: 6,
  },
  {
    id: '4',
    name: 'Jinja Nile View Cottage',
    location: 'Jinja, Uganda',
    type: 'Cottage',
    status: 'inactive',
    price: 120000,
    rating: 4.6,
    reviews: 22,
    occupancy: 0,
    image: 'https://images.unsplash.com/photo-1510798831971-661eb04b3739?w=400&q=80',
    imageAlt: 'Charming cottage with scenic views of the Nile River in Jinja',
    bedrooms: 2,
    bathrooms: 1,
    guests: 4,
  },
];

const statusColors = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-700',
};

export default function PropertyListings() {
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const filtered = filter === 'all' ? properties : properties.filter((p) => p.status === filter);

  return (
    <div>
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Properties', value: '4', sub: '+1 this month' },
          { label: 'Active Listings', value: '3', sub: '75% active rate' },
          { label: 'Avg Occupancy', value: '82%', sub: 'Last 30 days' },
          { label: 'Avg Rating', value: '4.8★', sub: 'Across all listings' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
            <div className="text-sm font-semibold text-gray-700 mt-0.5">{s.label}</div>
            <div className="text-xs text-gray-400 mt-1">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Filter + Add */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {(['all', 'active', 'inactive'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize transition-all ${
                filter === f ? 'bg-[#4088C6] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4088C6]/40'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <Link
          href="/host-dashboard/add-property"
          className="flex items-center gap-2 bg-[#4088C6] hover:bg-[#3070a6] text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
        >
          <span>+</span> Add Property
        </Link>
      </div>

      {/* Property cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="flex">
              <div className="w-36 h-36 flex-shrink-0 relative">
                <img src={p.image} alt={p.imageAlt} className="w-full h-full object-cover" />
                <span className={`absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-full ${statusColors[p.status]}`}>
                  {p.status}
                </span>
              </div>
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm leading-tight">{p.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{p.location} · {p.type}</p>
                  </div>
                  <Link
                    href={`/host-dashboard/property/${p.id}`}
                    className="text-gray-400 hover:text-[#4088C6] p-1 transition-colors"
                    title="Edit property"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </Link>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                  <span>🛏 {p.bedrooms} bed</span>
                  <span>🚿 {p.bathrooms} bath</span>
                  <span>👥 {p.guests} guests</span>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div>
                    <span className="text-sm font-bold text-gray-900">UGX {p.price.toLocaleString()}</span>
                    <span className="text-xs text-gray-400">/night</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-amber-500 font-semibold">★ {p.rating}</span>
                    <span className="text-gray-400">({p.reviews})</span>
                    {p.status === 'active' && (
                      <span className="text-emerald-600 font-semibold">{p.occupancy}% occ.</span>
                    )}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-50 flex gap-2">
                  <Link
                    href={`/host-dashboard/property/${p.id}`}
                    className="flex-1 text-center text-xs font-bold text-[#4088C6] hover:text-[#3070a6] bg-[#4088C6]/10 hover:bg-[#4088C6]/20 px-3 py-1.5 rounded-lg transition-all"
                  >
                    Edit Property
                  </Link>
                  <Link
                    href={`/property/${p.id}`}
                    className="flex-1 text-center text-xs font-bold text-gray-600 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-all"
                  >
                    View Listing
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
